/**
 * Phone utilities: region/phone code config, normalization, validation.
 * Used across patient forms, settings, etc.
 */
import axios from 'axios';
import { API_URL } from './apiConfig';

// Region config: region id -> { phoneCode, timezone, label }
export const REGIONS = {
  india: {
    id: 'india',
    label: 'India',
    phoneCode: '91',
    timezone: 'Asia/Kolkata',
    // Indian mobile: 10 digits, starts with 6-9
    phonePattern: /^[6-9]\d{9}$/,
    phonePatternLocal: /^0?[6-9]\d{9}$/,
    stripPrefix: /^0+/,
    minLen: 10,
    maxLen: 10,
  },
  israel: {
    id: 'israel',
    label: 'Middle East - Israel',
    phoneCode: '972',
    timezone: 'Asia/Jerusalem',
    // Israeli mobile: 9 digits after 0 (050, 052, 053, 054, etc.)
    phonePattern: /^[2-9]\d{8}$/,
    phonePatternLocal: /^0[2-9]\d{8}$/,
    stripPrefix: /^0/,
    minLen: 9,
    maxLen: 9,
  },
};

let phoneSettingsCache = {
  region: 'india',
  phoneCode: '91',
  timestamp: 0,
  cacheDuration: 2 * 60 * 1000,
};

/** Clear phone settings cache (e.g. after saving org settings so forms get fresh region). */
export const clearPhoneCache = () => {
  phoneSettingsCache.timestamp = 0;
};

/**
 * Get organization phone settings (region, phoneCode) from API
 * @returns {Promise<{ region: string, phoneCode: string }>}
 */
export const getOrganizationPhoneSettings = async (forceRefresh = false) => {
  const now = Date.now();
  if (!forceRefresh && phoneSettingsCache.timestamp && (now - phoneSettingsCache.timestamp) < phoneSettingsCache.cacheDuration) {
    return { region: phoneSettingsCache.region, phoneCode: phoneSettingsCache.phoneCode };
  }
  try {
    const token = localStorage.getItem('token');
    if (!token) return { region: 'india', phoneCode: '91' };
    const response = await axios.get(`${API_URL}/organizations/my`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.data.success && response.data.data?.settings) {
      const s = response.data.data.settings;
      let region = s.region;
      if (!region && (s.timezone === 'Asia/Jerusalem' || s.currency === 'ILS')) {
        region = 'israel';
      }
      region = region || 'india';
      const config = REGIONS[region] || REGIONS.india;
      phoneSettingsCache = {
        region,
        phoneCode: config.phoneCode,
        timestamp: now,
        cacheDuration: phoneSettingsCache.cacheDuration,
      };
      return { region, phoneCode: config.phoneCode };
    }
  } catch (err) {
    console.error('Error fetching organization phone settings:', err);
  }
  return { region: phoneSettingsCache.region, phoneCode: phoneSettingsCache.phoneCode };
};

/**
 * Format phone for display. If stored with full country code (e.g. 972526911090),
 * format as +972 526911090. Otherwise use org's code: +91 9876543210.
 * @param {string} phone - Stored digits (with or without country code)
 * @param {string} defaultCode - Org's phone code (91, 972)
 * @returns {string} e.g. "+972 526911090" or "+91 9876543210"
 */
export const formatPhoneForDisplay = (phone, defaultCode = '91') => {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('972') && digits.length >= 11 && digits.length <= 12) {
    return `+972 ${digits.slice(3)}`;
  }
  if (digits.startsWith('91') && digits.length === 12) {
    return `+91 ${digits.slice(2)}`;
  }
  return `+${defaultCode} ${digits}`;
};

/**
 * Normalize phone: if starts with +, treat as full international number and keep digits as-is.
 * Otherwise apply region-specific conversion (strip leading 0).
 * @param {string} value - Raw input (e.g. "+972526911090", "0526911090", "526911090")
 * @param {string} region - 'india' | 'israel'
 * @returns {string} Digits only
 */
export const normalizePhoneInput = (value, region = 'india') => {
  if (!value || typeof value !== 'string') return '';
  const trimmed = value.trim();
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  // If user entered with + prefix — full international number, store digits as-is
  if (trimmed.startsWith('+')) return digits;
  // Otherwise apply region-specific conversion (strip leading 0)
  const config = REGIONS[region] || REGIONS.india;
  return digits.replace(config.stripPrefix, '');
};

/**
 * Convert user input to stored format.
 * If user types +972526911090: store 972526911090 (as-is, full number).
 * If user types 0526911090 (Israel): store 526911090 (strip leading 0).
 * If user types 9876543210 (India): store 9876543210.
 * @param {string} value
 * @param {string} region
 * @returns {string}
 */
export const toStoredPhone = (value, region = 'india') => {
  return normalizePhoneInput(value, region);
};

/**
 * Validate phone for given region.
 * If value was entered with + prefix (international), accept 9-15 digits.
 * Otherwise validate against region-specific pattern (local format).
 * @param {string} value - Digits only
 * @param {string} region
 * @param {boolean} hadPlusPrefix - whether original input started with +
 * @returns {boolean}
 */
export const validatePhone = (value, region = 'india', hadPlusPrefix = false) => {
  if (!value) return false;
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return false;
  // Full international number (entered with +): 9-15 digits
  if (hadPlusPrefix) return digits.length >= 9 && digits.length <= 15;
  const config = REGIONS[region] || REGIONS.india;
  const stripped = digits.replace(config.stripPrefix, '');
  return config.phonePattern.test(stripped);
};

/**
 * Create Yup phone validation for given region
 * Usage: phone: getPhoneValidation(Yup, region)
 * @param {object} Yup - yup library
 * @param {string} region
 * @param {boolean} required - whether field is required
 * @returns {Yup.StringSchema}
 */
export const getPhoneValidation = (Yup, region, required = true) => {
  const config = REGIONS[region] || REGIONS.india;
  let schema = Yup.string().nullable();
  if (required) schema = schema.required('Phone number is required');
  return schema.test(
    'phone',
    `Phone number must be a valid ${config.label} number or full international (+code)`,
    (value) => {
      if (!value || !String(value).trim()) return !required;
      const hadPlus = String(value).trim().startsWith('+');
      const normalized = toStoredPhone(value, region);
      return validatePhone(normalized, region, hadPlus);
    }
  );
};
